<script setup>
/**
 * WhatsappCostNotice.vue
 *
 * Contextual notice that sending WhatsApp messages consumes credit, with the
 * per-message cost(s) configured in Administration. Renders nothing unless
 * WhatsApp billing is enabled and the relevant cost is > 0, so it appears and
 * disappears automatically with the admin pricing toggle.
 *
 * Reused at every surface where the user is about to do something that costs:
 * channel card, channel config modal, template page, send-message / send-template
 * flow node editors (also surfaced in tools and webhooks via the same editors).
 *
 * @example
 * <WhatsappCostNotice kind="template" />
 * <WhatsappCostNotice kind="session" />
 * <WhatsappCostNotice />  <!-- both -->
 */

import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWhatsappPricing } from '../../composables/useWhatsappPricing';

const props = defineProps({
  /** Which cost(s) to surface: 'template' | 'session' | 'both'. */
  kind: {
    type: String,
    default: 'both',
    validator: (v) => ['template', 'session', 'both'].includes(v),
  },
});

const { t } = useI18n();
const { pricing, fetchPricing } = useWhatsappPricing();

onMounted(fetchPricing);

const fmt = (eur) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(Number(eur) || 0);

const hasTemplate = computed(() => pricing.value.templateCostEur > 0);
const hasSession = computed(() => pricing.value.sessionMessageCostEur > 0);

const templateCost = computed(() => fmt(pricing.value.templateCostEur));
const sessionCost = computed(() => fmt(pricing.value.sessionMessageCostEur));

const visible = computed(() => {
  if (!pricing.value.enabled) return false;
  if (props.kind === 'template') return hasTemplate.value;
  if (props.kind === 'session') return hasSession.value;
  return hasTemplate.value || hasSession.value;
});

const message = computed(() => {
  if (props.kind === 'template') return t('whatsapp.costNotice.template', { cost: templateCost.value });
  if (props.kind === 'session') return t('whatsapp.costNotice.session', { cost: sessionCost.value });
  // 'both': if only one cost is set, show only that line.
  if (hasTemplate.value && hasSession.value) {
    return t('whatsapp.costNotice.both', { template: templateCost.value, session: sessionCost.value });
  }
  if (hasTemplate.value) return t('whatsapp.costNotice.template', { cost: templateCost.value });
  return t('whatsapp.costNotice.session', { cost: sessionCost.value });
});
</script>

<template>
  <div
    v-if="visible"
    class="flex items-start gap-2 rounded-md border border-blue-700/60 bg-blue-900/30 px-3 py-2 text-xs text-blue-200"
    role="note"
  >
    <span class="material-icons text-sm text-blue-300 shrink-0" aria-hidden="true">payments</span>
    <span>{{ message }}</span>
  </div>
</template>
